import { useEffect } from 'react';

import { useAccountCompanyId } from 'application/hooks';

import { PaymentScheme, useBillingCompanyBillingInfoQuery } from '@shared/api/graphql/generated';
import { useSubscriptionCost } from '@entities/billing';
import { FetchPolicyKeys } from '@shared/api/graphql/types';

import DateUtils from '@zvuk-b2b/react-uikit/toolkit/DateUtils';

import { AmountProps, UseAmountViewModel } from './types';

const useAmountViewModel = ({ selectedPriceItemPeriodId, currentPeriodId }: AmountProps): UseAmountViewModel | null => {
  const companyId = useAccountCompanyId();

  const {
    data: billingInfoData,
    loading,
    error,
  } = useBillingCompanyBillingInfoQuery({
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
    variables: { companyId },
  });

  const [requestCost, subscriptionCostResult] = useSubscriptionCost();

  useEffect(() => {
    if (!billingInfoData) return;

    requestCost.subscriptionCostByPricePlanPeriod(companyId, selectedPriceItemPeriodId);
  }, [selectedPriceItemPeriodId, billingInfoData]);

  if (!billingInfoData || !billingInfoData.result || loading || error) {
    return null;
  }

  const {
    nextBillingAmount,
    discount,
    hasInactiveLicenses,
    totalLicenses,
    fullPeriodAmount,
    fullPeriodAmountWithDiscount,
    paymentScheme,
    nextBillingDate,
  } = billingInfoData.result;

  const withDiscountAndCurrentSelected = discount && currentPeriodId === selectedPriceItemPeriodId;

  const startDate = new Date(nextBillingDate);
  const endDate = DateUtils.getLastDayOfMonth(startDate);

  const formatStartNextBillingDate = DateUtils.getFormatDateFrom(startDate, 'dd.MM.yyyy');
  const formatEndNextBillingDate = DateUtils.getFormatDateFrom(endDate, 'dd.MM.yyyy');

  const formatStartNextBillingDateShort = DateUtils.getFormatDateFrom(startDate, 'dd.MM.yy');
  const formatEndNextBillingDateShort = DateUtils.getFormatDateFrom(endDate, 'dd.MM.yy');

  return {
    nextBillingAmount: nextBillingAmount || 0,
    fullPeriodAmount: fullPeriodAmount || 0,
    fullPeriodAmountWithDiscount: fullPeriodAmountWithDiscount || 0,
    licenseCostLoading: subscriptionCostResult.loading || loading,
    subscriptionCostByPricePlanPeriod: subscriptionCostResult.data.subscriptionCostByPricePlanPeriod || 0,
    withDiscountAndCurrentSelected: withDiscountAndCurrentSelected === true,
    withInactiveLicensesInfo: hasInactiveLicenses && totalLicenses?.length > 0,
    withResidualPayment: paymentScheme === PaymentScheme.InvoiceCalendarMonthly && startDate.getDate() !== 1,
    formatNextBillingDate: `/ ${formatStartNextBillingDate} - ${formatEndNextBillingDate}`,
    formatNextBillingDateShort: `/ ${formatStartNextBillingDateShort} - ${formatEndNextBillingDateShort}`,
  };
};

export default useAmountViewModel;
